import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, AsyncStatus } from '@algoan/dashboard/model';

export type UserErrorMessages = 'genericError';

export interface UserReducerState {
  user: User | undefined;
  usersList: User[];
  userFetchingStatus: AsyncStatus;
  userError: UserErrorMessages | undefined;
}

const getInitialState = (): UserReducerState => ({
  user: undefined,
  usersList: [],
  userFetchingStatus: AsyncStatus.IDLE,
  userError: undefined,
});

const initialState = getInitialState();

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserState: () => initialState,
    fetchUserStart(state) {
      state.userFetchingStatus = AsyncStatus.PENDING;
      state.userError = undefined;
    },
    fetchUserSuccess(state, action: PayloadAction<User>) {
      state.user = action.payload;
      state.userFetchingStatus = AsyncStatus.RESOLVED;
      state.userError = undefined;
    },
    fetchUsersListSuccess(state, action: PayloadAction<User[]>) {
      state.usersList = action.payload;
      state.userFetchingStatus = AsyncStatus.RESOLVED;
      state.userError = undefined;
    },
    updateUserWithNewAttributesSuccess(state, action: PayloadAction<User>) {
      const user = state.user;
      const newUser = action.payload;
      if (user) {
        state.user = {
          ...user,
          shouldBeNotifiedOnAnalysisCreated:
            newUser.shouldBeNotifiedOnAnalysisCreated ?? user.shouldBeNotifiedOnAnalysisCreated ?? false,
          shouldBeNotifiedOnEveryAnalysisCreatedOfOrganization:
            newUser.shouldBeNotifiedOnEveryAnalysisCreatedOfOrganization ??
            user.shouldBeNotifiedOnEveryAnalysisCreatedOfOrganization ??
            false,
        };
      } else {
        state.user = newUser;
      }
      state.userFetchingStatus = AsyncStatus.RESOLVED;
      state.userError = undefined;
    },
    fetchUserFailure(state, action: PayloadAction<{ error: UserErrorMessages }>) {
      state.userFetchingStatus = AsyncStatus.REJECTED;
      state.userError = action.payload.error;
    },
  },
});

export const { reducer: userReducer } = userSlice;

export const {
  resetUserState,
  fetchUserStart,
  fetchUserSuccess,
  updateUserWithNewAttributesSuccess,
  fetchUsersListSuccess,
  fetchUserFailure,
} = userSlice.actions;
